import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { Phone, PhoneFormData } from '../types/Phone';
import { User } from '../types/User';
import PhoneService from '../services/PhoneService';
import UserService from '../services/UserService';

interface PhoneDialogProps {
  open: boolean;
  phone: Phone | null;
  isEditing: boolean;
  onClose: () => void;
  onSave: (phoneData: PhoneFormData) => void;
}

const PhoneDialog: React.FC<PhoneDialogProps> = ({
  open,
  phone,
  isEditing,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<PhoneFormData>({
    macAddress: '',
    userId: '',
    description: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Benutzer laden beim Öffnen des Dialogs
  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open]);

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const userList = await UserService.getAllUsers();
      setUsers(userList);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (phone && isEditing) {
      setFormData({
        macAddress: phone.macAddress,
        userId: phone.userId,
        description: phone.description || ''
      });
      // Benutzer basierend auf userId finden
      const user = users.find(u => u.userId === phone.userId);
      setSelectedUser(user || null);
    } else {
      setFormData({
        macAddress: '',
        userId: '',
        description: ''
      });
      setSelectedUser(null);
    }
    setErrors({});
  }, [phone, isEditing, open, users]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // MAC-Adresse validieren
    if (!formData.macAddress.trim()) {
      newErrors.macAddress = 'MAC-Adresse ist erforderlich';
    } else if (!/^([0-9A-F]{2}[:-]){5}([0-9A-F]{2})$/i.test(formData.macAddress)) {
      newErrors.macAddress = 'Ungültiges MAC-Adresse Format. Erwartet: XX:XX:XX:XX:XX:XX';
    }

    // Benutzer-ID validieren
    if (!formData.userId.trim()) {
      newErrors.userId = 'Benutzer-ID ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave({
        ...formData,
        macAddress: formData.macAddress.toUpperCase().replace(/[^0-9A-F]/g, '').replace(/(.{2})/g, '$1:').slice(0, -1)
      });
    }
  };

  const handleChange = (field: keyof PhoneFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    
    // Fehler für dieses Feld löschen
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const handleUserChange = (event: any, newValue: User | null) => {
    setSelectedUser(newValue);
    setFormData({
      ...formData,
      userId: newValue ? newValue.userId : ''
    });
    
    // Fehler für userId löschen
    if (errors.userId) {
      setErrors({
        ...errors,
        userId: ''
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Telefon bearbeiten' : 'Neues Telefon hinzufügen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="MAC-Adresse"
            placeholder="z.B. AA:BB:CC:DD:EE:FF"
            value={formData.macAddress}
            onChange={handleChange('macAddress')}
            error={!!errors.macAddress}
            helperText={errors.macAddress || 'Format: XX:XX:XX:XX:XX:XX'}
            margin="normal"
            required
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          
          <Autocomplete
            fullWidth
            options={users}
            getOptionLabel={(option) => `${option.name} (${option.userId})`}
            value={selectedUser}
            onChange={handleUserChange}
            loading={usersLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Benutzer"
                placeholder="Benutzer auswählen..."
                error={!!errors.userId}
                helperText={errors.userId || 'Wählen Sie den Benutzer aus, dem dieses Telefon zugeordnet werden soll'}
                margin="normal"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.userId} • {UserService.getUserRoleLabel(option.groups)}
                  </Typography>
                </Box>
              </Box>
            )}
            noOptionsText={usersLoading ? "Benutzer werden geladen..." : "Keine Benutzer gefunden"}
          />
          
          <TextField
            fullWidth
            label="Beschreibung (Optional)"
            placeholder="z.B. Büro Empfang, Konferenzraum A, etc."
            value={formData.description}
            onChange={handleChange('description')}
            margin="normal"
            multiline
            rows={2}
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Hinweis:</strong> Die MAC-Adresse wird automatisch normalisiert 
              (Großbuchstaben, Doppelpunkt-Format). Wählen Sie den Benutzer aus der Liste aus, 
              dem dieses Telefon zugeordnet werden soll.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Abbrechen
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          {isEditing ? 'Speichern' : 'Hinzufügen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhoneDialog;
